import { useEffect, useState } from 'react'

import { HelpOutline as HelpIcon } from '@mui/icons-material'
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Typography,
} from '@mui/material'

import { DemographicsPicker } from 'components'
import { t } from 'i18n'

export default function ManageRecognitionWhoIsThisEventFor({
  formik,
  demographics,
  demographicsIsLoading,
}) {
  const [hintOpen, setHintOpen] = useState(false)

  const hasSelectedDemographics = !!formik.values.demographics?.some(
    (d: any) => d?.demographics?.length > 0,
  )

  useEffect(() => {
    if (
      !hasSelectedDemographics &&
      formik.values.requires_matching_demographic_values
    ) {
      formik.setFieldValue('requires_matching_demographic_values', false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSelectedDemographics])

  const matchingCheckbox = hasSelectedDemographics ? (
    <Box display='flex' alignItems='center'>
      <FormControlLabel
        control={
          <Checkbox
            checked={!!formik.values.requires_matching_demographic_values}
            onChange={() =>
              formik.setFieldValue(
                'requires_matching_demographic_values',
                !formik.values.requires_matching_demographic_values,
              )
            }
            name='requires_matching_demographic_values'
            color='primary'
            data-cy='requiresMatchingDemographicValues'
          />
        }
        label={t('recognitions.fields.requireMatchingDemographicValues')}
      />
      <IconButton
        size='small'
        onClick={() => setHintOpen(true)}
        aria-label='matching demographics help'
        data-cy='requiresMatchingDemographicValuesHelp'
      >
        <HelpIcon fontSize='small' />
      </IconButton>
    </Box>
  ) : null

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <DemographicsPicker
          key='demographics'
          fieldName='demographics'
          formik={formik}
          demographics={demographics}
          demographicsIsLoading={demographicsIsLoading}
          label={t('recognitions.fields.whoIsThisEventFor')}
          selectAllOnAdd
          headerSlot={matchingCheckbox}
        />
      </Grid>

      <Dialog
        open={hintOpen}
        onClose={() => setHintOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {t('recognitions.fields.requireMatchingDemographicValues')}
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' paragraph>
            {t('recognitions.fields.requireMatchingDemographicValuesHelp')}
          </Typography>
          <Typography variant='body2' color='text.secondary' paragraph>
            {t('recognitions.fields.requireMatchingDemographicValuesExample')}
          </Typography>
          <Box display='flex' justifyContent='center' my={2}>
            <MatchingDemographicsSVG />
          </Box>
        </DialogContent>
      </Dialog>
    </Grid>
  )
}

function MatchingDemographicsSVG() {
  return (
    <svg
      width='420'
      height='220'
      viewBox='0 0 420 220'
      xmlns='http://www.w3.org/2000/svg'
      role='img'
      aria-label='Department A users cannot recognize Department B users'
    >
      <defs>
        <marker
          id='arrowhead-forbidden'
          markerWidth='10'
          markerHeight='10'
          refX='8'
          refY='3'
          orient='auto'
        >
          <path d='M0,0 L0,6 L9,3 z' fill='#9e9e9e' />
        </marker>
      </defs>

      {/* Department A box */}
      <rect
        x='15'
        y='40'
        width='150'
        height='140'
        rx='10'
        fill='#e3f2fd'
        stroke='#1976d2'
        strokeWidth='2'
      />
      <text
        x='90'
        y='62'
        textAnchor='middle'
        fontFamily='sans-serif'
        fontSize='14'
        fontWeight='600'
        fill='#1976d2'
      >
        Department A
      </text>
      {/* User icons for Department A */}
      <g transform='translate(45,90)'>
        <circle cx='15' cy='12' r='10' fill='#1976d2' />
        <path d='M0,40 C0,25 30,25 30,40 Z' fill='#1976d2' />
      </g>
      <g transform='translate(105,90)'>
        <circle cx='15' cy='12' r='10' fill='#1976d2' />
        <path d='M0,40 C0,25 30,25 30,40 Z' fill='#1976d2' />
      </g>

      {/* Department B box */}
      <rect
        x='255'
        y='40'
        width='150'
        height='140'
        rx='10'
        fill='#fce4ec'
        stroke='#c2185b'
        strokeWidth='2'
      />
      <text
        x='330'
        y='62'
        textAnchor='middle'
        fontFamily='sans-serif'
        fontSize='14'
        fontWeight='600'
        fill='#c2185b'
      >
        Department B
      </text>
      {/* User icons for Department B */}
      <g transform='translate(285,90)'>
        <circle cx='15' cy='12' r='10' fill='#c2185b' />
        <path d='M0,40 C0,25 30,25 30,40 Z' fill='#c2185b' />
      </g>
      <g transform='translate(345,90)'>
        <circle cx='15' cy='12' r='10' fill='#c2185b' />
        <path d='M0,40 C0,25 30,25 30,40 Z' fill='#c2185b' />
      </g>

      {/* Arrow between departments */}
      <line
        x1='170'
        y1='110'
        x2='250'
        y2='110'
        stroke='#9e9e9e'
        strokeWidth='3'
        markerEnd='url(#arrowhead-forbidden)'
      />

      {/* Forbidden X overlay over the arrow */}
      <line
        x1='196'
        y1='96'
        x2='224'
        y2='124'
        stroke='#d32f2f'
        strokeWidth='5'
        strokeLinecap='round'
      />
      <line
        x1='224'
        y1='96'
        x2='196'
        y2='124'
        stroke='#d32f2f'
        strokeWidth='5'
        strokeLinecap='round'
      />

      {/* Caption */}
      <text
        x='210'
        y='205'
        textAnchor='middle'
        fontFamily='sans-serif'
        fontSize='12'
        fill='#616161'
      >
        Cross-department recognition is blocked when enabled
      </text>
    </svg>
  )
}
